import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import Filters from "components/Filters";
import FiltersContentWrapper from "components/Filters/ContentWrapper";
import FiltersPanel from "components/Filters/FiltersPanel";
import { useCachedFilterFields } from "components/Filters/hooks";
import FiltersSidebar from "components/Filters/Sidebar";
import SortDropdown from "components/Filters/SortDropdown";
import FiltersSplit from "components/Filters/Split";
import { SavedFilterView, SortOption } from "components/Filters/types";
import { Collapse, Expand } from "components/icons/generated";
import ListContentWrapper from "components/ListContentWrapper";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Toggle from "ds/components/Toggle";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import { SearchSuggestionsOutput } from "types/generated";
import useMakeFiltersData from "components/Filters/useMakeFiltersData";

import {
  FILTERS_ORDER_SETTINGS_KEY,
  initialSortDirection,
  initialSortOption,
  searchStackRunChangesSuggestionsDictionary,
} from "../constants";
import { useChangesStore } from "../useChangesStore";
import { getFilterKey } from "./helpers";
import styles from "./styles.module.css";

type FiltersLayoutProps = {
  allSelected: boolean;
  onSelectAll: () => void;
  onResetAll: () => void;
  children: React.ReactNode;
  hasItems: boolean;
  currentSavedView?: SavedFilterView;
  setCurrentSavedView: (view?: SavedFilterView) => unknown;
  suggestions?: SearchSuggestionsOutput;
  loading: boolean;
  handlePollingActiveSections: (fields: string[]) => void;
  selectable: boolean;
  calloutSlot?: React.ReactNode;
};

const FiltersLayout = ({
  allSelected,
  onSelectAll,
  onResetAll,
  children,
  hasItems,
  currentSavedView,
  setCurrentSavedView,
  suggestions,
  loading,
  handlePollingActiveSections,
  selectable,
  calloutSlot,
}: FiltersLayoutProps) => {
  const cachedFiltersData = useCachedFilterFields(suggestions?.fields);

  const [collapseAll, expandAll, hasCollapsedChanges] = useChangesStore(
    useShallow((state) => [state.collapseAll, state.expandAll, state.hasCollapsedChanges()])
  );

  const sortOptions = useMemo((): SortOption[] => {
    return (
      cachedFiltersData
        .filter((field) => field.orderable)
        .map((field) => {
          return {
            value: field.name,
            label: getFilterKey(field.name),
          };
        }) || []
    );
  }, [cachedFiltersData]);

  const { filters, filterOptionsMap } = useMakeFiltersData({
    suggestions: cachedFiltersData,
    dictionary: searchStackRunChangesSuggestionsDictionary,
  });

  const handleSelectAllToggle = () => {
    if (!allSelected) {
      onSelectAll();
    } else {
      onResetAll();
    }
  };

  return (
    <Filters
      filters={filters}
      filtersItemsOptionsMap={filterOptionsMap}
      filtersLoading={loading}
      sortOptions={sortOptions}
      initialSortOption={initialSortOption}
      initialSortDirection={initialSortDirection}
      filtersDictionary={searchStackRunChangesSuggestionsDictionary}
      pollActiveSections={handlePollingActiveSections}
      filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
      filtersType="stackRunChanges"
      currentSavedView={currentSavedView}
      setCurrentSavedView={setCurrentSavedView}
    >
      <FiltersSplit>
        <FiltersSidebar analyticsPage={AnalyticsPageStack.StacksRunChanges} />

        <FiltersContentWrapper>
          {calloutSlot}

          <FiltersPanel>
            <Box direction="row" gap="medium">
              {hasItems && selectable && (
                <Toggle
                  variant="checkbox"
                  id="select_all"
                  isSelected={allSelected}
                  onChange={handleSelectAllToggle}
                  aria-label={allSelected ? "Unselect all" : "Select all"}
                >
                  Select all
                </Toggle>
              )}
              <Button
                variant="ghost"
                size="small"
                startIcon={hasCollapsedChanges ? Expand : Collapse}
                onPress={hasCollapsedChanges ? expandAll : collapseAll}
              >
                {hasCollapsedChanges ? "Expand all" : "Collapse all"}
              </Button>
            </Box>

            <SortDropdown />
          </FiltersPanel>

          <ListContentWrapper className={styles.content}>{children}</ListContentWrapper>
        </FiltersContentWrapper>
      </FiltersSplit>
    </Filters>
  );
};

export default FiltersLayout;
